import { useState } from 'react'
import { Box, Checkbox, Typography } from '@mui/material'
import { generatePath, useNavigate } from 'react-router'
import { toast } from 'react-toastify'
import { type SnapshotDashboardModel } from '../models/SnapshotDashboardModel'
import { CompleteOutstandingAssessments, FinalizeSnapshot } from '../services/CETAClassroomService'
import { Modal } from '../pages/customComponents/Modal'
import { AppRoutes } from '../pages/Routes'

type FinalizePath = 'finalizeDirectly' | 'reconcile' | 'completeOutstandingThenReconcile'

interface Args {
  classroomId: string
  activeSnapshot: SnapshotDashboardModel | null
  completedSnapshots: SnapshotDashboardModel[]
  cetaUpdateBegin: Date
  refreshData: () => void
}

interface FinalizeSnapshotFlow {
  startFinalizing: () => void
  finalizeModals: JSX.Element | null
}

export const useFinalizeSnapshot = (args: Args): FinalizeSnapshotFlow => {
  const { classroomId, activeSnapshot, completedSnapshots, cetaUpdateBegin, refreshData } = args
  const nav = useNavigate()
  const [isFinalizing, setIsFinalizing] = useState<boolean>(false)
  const [treatAsUpdate, setTreatAsUpdate] = useState<boolean>(activeSnapshot?.isCETAUpdate ?? false)

  const startFinalizing = (): void => {
    setTreatAsUpdate(activeSnapshot?.isCETAUpdate ?? false)
    setIsFinalizing(true)
  }

  const goToReconcileScreen = (snapshotId: number): void => {
    const route = generatePath(AppRoutes.CETA_RECONCILE,
      { classroomId, snapshotId: snapshotId.toString() })
    nav(route)
  }

  const finalizeSnapshot = async (): Promise<void> => {
    await FinalizeSnapshot(activeSnapshot?.id.toString() ?? '', treatAsUpdate)
    toast.success('Snapshot finalized')
    refreshData()
  }

  const completeOutstandingThenReconcile = async (snapshotId: number): Promise<void> => {
    await CompleteOutstandingAssessments(snapshotId.toString())
    goToReconcileScreen(snapshotId)
  }

  const completedMemberNames = activeSnapshot?.completedMemberNames ?? []
  const incompleteMemberNames = activeSnapshot?.incompleteMemberNames ?? []

  const incompleteMembersNames = (): string => {
    if (incompleteMemberNames.length === 0) {
      return ''
    }

    if (incompleteMemberNames.length === 1) {
      return incompleteMemberNames[0]
    }

    const names = [...incompleteMemberNames]
    const last = names.pop() ?? ''
    return `${names.join(', ')} and ${last}`
  }

  const submissionCount = completedMemberNames.length
  const hasIncompleteMembers = incompleteMemberNames.length > 0
  const finalizePath: FinalizePath = submissionCount === 1
    ? 'finalizeDirectly'
    : hasIncompleteMembers ? 'completeOutstandingThenReconcile' : 'reconcile'

  const hasCompletedUpdate = completedSnapshots.some(s => s.isCETAUpdate)
  const canAddCETAUpdate = cetaUpdateBegin < new Date() && !hasCompletedUpdate

  if (!isFinalizing || activeSnapshot == null) {
    return { startFinalizing, finalizeModals: null }
  }

  const finalizeModals = (
    <>
      {finalizePath === 'finalizeDirectly' &&
        <Modal
          open={true}
          title='Finalize Snapshot'
          maxWidth='sm'
          bodyContent={
            <>
              {hasIncompleteMembers
                ? <Typography variant='body1'>Only {completedMemberNames[0]} has completed their CETA. Finalizing will lock this snapshot and block {incompleteMembersNames()} from completing theirs. Are you ready to finalize?</Typography>
                : <Typography variant='body1'>This will finalize and lock this CETA Snapshot. Are you ready to finalize?</Typography>
              }

              {canAddCETAUpdate &&
              !activeSnapshot.isCETAUpdate &&
              !activeSnapshot.isKickoff &&
                <Box sx={{ display: 'flex', mt: '1em' }}>
                  <Checkbox
                    checked={treatAsUpdate}
                    sx={{ color: 'var(--grey-500) !important' }}
                    onClick={() => { setTreatAsUpdate(!treatAsUpdate) }}
                    inputProps={{ 'aria-label': 'Treat as CETA Update' }}
                    data-testid='treatAsUpdate'
                  />
                  <Typography variant='body1' ml='.5em' alignContent='center'>Treat as CETA Update</Typography>
                </Box>
              }
            </>
          }
          confirmButtonText='Yes, Finalize'
          cancelButtonText='No, Cancel'
          onClose={() => { setIsFinalizing(false) }}
          onConfirm={finalizeSnapshot}
        />
      }

      {finalizePath === 'reconcile' &&
        <Modal
          open={true}
          title='Finalize Snapshot'
          bodyContent={'This will finalize and lock this CETA Snapshot. Are you ready to reconcile the teams\' responses?'}
          confirmButtonText='Yes, Continue'
          cancelButtonText='No, Cancel'
          onClose={() => { setIsFinalizing(false) }}
          onConfirm={() => { goToReconcileScreen(activeSnapshot.id) }}
        />
      }

      {finalizePath === 'completeOutstandingThenReconcile' &&
        <Modal
          open={true}
          title='Finalize Snapshot'
          bodyContent={`There are incomplete instances by ${incompleteMembersNames()}. By continuing, this will block them from completing their instance of the CETA. Are you sure you want to continue to reconcile the teams' responses?`}
          confirmButtonText='Yes, Continue'
          cancelButtonText='No, Cancel'
          onClose={() => { setIsFinalizing(false) }}
          onConfirm={async () => { await completeOutstandingThenReconcile(activeSnapshot.id) }}
        />
      }
    </>
  )

  return { startFinalizing, finalizeModals }
}
